import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import PlatformText from './PlatformText';
import PlatformButton from './PlatformButton';
import PlatformLayout from './PlatformLayout';
import PlatformCard from './PlatformCard';
import { getPlatformType, getPlatformStyles } from '../utils/platformUtils';
import CrashlyticsService from '../services/CrashlyticsService.js';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Report to Crashlytics
    try {
      const crashlyticsService = CrashlyticsService.getInstance();
      crashlyticsService.recordError(error, 'ErrorBoundary');
      
      // Add additional context
      crashlyticsService.setCustomKeys([
        { key: 'error_boundary_component', value: this.props.componentName || 'Unknown' },
        { key: 'error_boundary_stack', value: errorInfo?.componentStack || 'No stack trace' },
        { key: 'platform_type', value: getPlatformType() },
        { key: 'user_agent', value: navigator.userAgent },
        { key: 'timestamp', value: new Date().toISOString() }
      ]);
      
      crashlyticsService.log(`ErrorBoundary caught error in ${this.props.componentName || 'Unknown component'}: ${error.message}`);
    } catch (crashlyticsError) {
      // Failed to report error to Crashlytics - handled silently
    }
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

// Functional component for error fallback UI with multi-platform support
const ErrorFallback = ({ error, errorInfo }) => {
  const { t } = useTranslation('errors');
  const platformType = getPlatformType();

  // Platform-specific error handling
  const handleRefresh = () => {
    if (platformType === 'ios' || platformType === 'android') {
      // For native platforms, try to navigate back or restart
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.reload();
      }
    } else {
      // For web platforms, reload the page
      window.location.reload();
    }
  };

  const handleRetry = () => {
    // Try to recover by clearing error state
    window.location.reload();
  };

  return (
    <PlatformLayout>
      <PlatformCard 
        variant="outlined" 
        padding="large"
        style={{
          maxWidth: '600px',
          margin: '20px auto',
          borderColor: '#ef4444',
          backgroundColor: '#fef2f2'
        }}
      >
        {/* Error Icon */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '48px', 
          marginBottom: '16px',
          color: '#ef4444'
        }}>
          {platformType === 'ios' ? '⚠️' : platformType === 'android' ? '⚠️' : '⚠️'}
        </div>

        {/* Error Title */}
        <PlatformText 
          variant="h2" 
          align="center" 
          color="danger"
          style={{ marginBottom: '16px' }}
        >
          {t('errorBoundary.title')}
        </PlatformText>

        {/* Error Message */}
        <PlatformText 
          variant="body" 
          align="center" 
          style={{ marginBottom: '24px', color: '#7f1d1d' }}
        >
          {t('errorBoundary.message')}
        </PlatformText>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <PlatformButton 
            onClick={handleRefresh}
            variant="primary"
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              minWidth: '120px'
            }}
          >
            <PlatformText translationKey="errorBoundary.refreshPage" namespace="errors" />
          </PlatformButton>

          <PlatformButton 
            onClick={handleRetry}
            variant="outline"
            style={{
              borderColor: '#ef4444',
              color: '#ef4444',
              minWidth: '120px'
            }}
          >
            <PlatformText translationKey="general.retry" namespace="errors" />
          </PlatformButton>
        </div>


        {/* Platform-specific additional info */}
        <div style={{ 
          marginTop: '16px', 
          textAlign: 'center',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <PlatformText variant="small">
            Platform: {platformType} | Time: {new Date().toLocaleString()}
          </PlatformText>
        </div>
      </PlatformCard>
    </PlatformLayout>
  );
};

const ErrorBoundary = (props) => {
  return <ErrorBoundaryClass {...props} />;
};

export default ErrorBoundary;